import React from "react";
import {
  Paper,
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Tooltip,
  Skeleton,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import SentimentVeryDissatisfiedIcon from "@mui/icons-material/SentimentVeryDissatisfied";
import WinnerLoser from "./WinnerLoser";
import { PlayerScore } from "../types/score";
import { Rules } from "../types/rules";
import { SelectedGame } from "../types/game";

interface ScoreboardTableProps {
  scores: PlayerScore[];
  weekComplete?: boolean;
  selectedYear: number;
  selectedWeek?: number;
  rules?: Rules;
  selectedGames?: SelectedGame[];
  gameOfTheWeek?: number | null;
  getDisplayName: (email: string) => string;
  isMobile: boolean;
  loading?: boolean;
  userEmail?: string;
  viewMode?: "week" | "year";
}

const ScoreboardTable: React.FC<ScoreboardTableProps> = ({
  scores,
  weekComplete = false,
  selectedYear,
  selectedWeek = 0,
  rules,
  selectedGames = [],
  gameOfTheWeek = null,
  getDisplayName,
  isMobile,
  loading = false,
  userEmail,
  viewMode = "week",
}) => {
  if (loading || scores.length === 0) {
    return <ScoreboardTableSkeleton isMobile={isMobile} />;
  }
  return (
    <Paper elevation={2}>
      <Box
        sx={{
          background:
            viewMode === "year"
              ? "linear-gradient(135deg, #2e7d32, #66bb6a)"
              : "linear-gradient(135deg, #ff9800, #ffb74d)",
          color: "white",
          padding: isMobile ? 1.5 : 2,
        }}
      >
        <Typography
          variant={isMobile ? "subtitle1" : "h6"}
          sx={{ fontWeight: 600 }}
        >
          {viewMode === "year"
            ? `${selectedYear} Standings`
            : selectedYear > 0 && selectedWeek > 0
            ? `${selectedYear} - Week ${selectedWeek} Results`
            : "This Week Results"}
          {weekComplete && viewMode === "week" && (
            <Chip
              label="Complete"
              size="small"
              sx={{
                marginLeft: 1,
                backgroundColor: "rgba(255,255,255,0.2)",
                color: "white",
              }}
            />
          )}
        </Typography>
      </Box>

      {/* Winner/Loser Section */}
      {((viewMode === "week" && weekComplete) || viewMode === "year") && (
        <WinnerLoser
          scores={scores}
          viewMode={viewMode}
          userEmail={userEmail}
        />
      )}

      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 600 }}>Rank</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Player</TableCell>
              <TableCell align="right" sx={{ fontWeight: 600 }}>
                Points
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 600 }}>
                Correct
              </TableCell>
              {rules?.nailRules && (
                <TableCell align="right" sx={{ fontWeight: 600 }}>
                  Nails
                </TableCell>
              )}
              {rules?.lockRules && (
                <TableCell align="right" sx={{ fontWeight: 600 }}>
                  Locks
                </TableCell>
              )}
              {viewMode === "year" ? (
                <>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>
                    Wins
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>
                    Losses
                  </TableCell>
                </>
              ) : (
                rules?.gameOfTheWeekRules && (
                  <TableCell align="right" sx={{ fontWeight: 600 }}>
                    Game of the Week
                  </TableCell>
                )
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {scores.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={
                    viewMode === "year"
                      ? rules?.lockRules
                        ? 8
                        : 7
                      : rules?.nailRules &&
                        rules?.lockRules &&
                        rules?.gameOfTheWeekRules
                      ? 7
                      : rules?.nailRules && rules?.lockRules
                      ? 6
                      : rules?.nailRules && rules?.gameOfTheWeekRules
                      ? 6
                      : rules?.lockRules && rules?.gameOfTheWeekRules
                      ? 6
                      : rules?.nailRules ||
                        rules?.lockRules ||
                        rules?.gameOfTheWeekRules
                      ? 5
                      : 4
                  }
                >
                  <Typography
                    variant="body2"
                    sx={{
                      textAlign: "center",
                      color: "#666",
                      padding: 2,
                    }}
                  >
                    {viewMode === "year"
                      ? "No player scores available for this year."
                      : "No player scores available for this week."}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              (() => {
                return scores
                  .sort((a, b) => (a.rank ?? 0) - (b.rank ?? 0))
                  .map((player, index) => {
                    // Use backend-provided wins/losses fields instead of calculating
                    const isWinner = weekComplete && (player.wins || 0) > 0;
                    const isLoser = weekComplete && (player.losses || 0) > 0;

                    return (
                      <TableRow
                        key={player.email}
                        sx={{
                          backgroundColor: isWinner
                            ? "rgba(245, 158, 11, 0.1)"
                            : isLoser
                            ? "rgba(220, 38, 38, 0.1)"
                            : "transparent",
                          "&:hover": {
                            backgroundColor: isWinner
                              ? "rgba(245, 158, 11, 0.15)"
                              : isLoser
                              ? "rgba(220, 38, 38, 0.15)"
                              : "rgba(0, 0, 0, 0.04)",
                          },
                        }}
                      >
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {player.rank || index + 1}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            <Typography variant="body2">
                              {getDisplayName(player.email)}
                            </Typography>
                            {weekComplete && (
                              <>
                                {isWinner && (
                                  <Tooltip title="Week Winner! 👑" arrow>
                                    <EmojiEventsIcon
                                      sx={{
                                        color: "#f59e0b",
                                        fontSize: 20,
                                        filter:
                                          "drop-shadow(0 1px 2px rgba(0,0,0,0.1))",
                                      }}
                                    />
                                  </Tooltip>
                                )}
                                {isLoser && (
                                  <Tooltip title="Week Loser 🤡" arrow>
                                    <SentimentVeryDissatisfiedIcon
                                      sx={{
                                        color: "#dc2626",
                                        fontSize: 20,
                                        filter:
                                          "drop-shadow(0 1px 2px rgba(0,0,0,0.1))",
                                      }}
                                    />
                                  </Tooltip>
                                )}
                              </>
                            )}
                          </Box>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {player.points}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2">
                            {player.correctGames}
                          </Typography>
                        </TableCell>
                        {rules?.nailRules && (
                          <TableCell align="right">
                            <Typography variant="body2">
                              {player.nails || 0}
                            </Typography>
                          </TableCell>
                        )}
                        {rules?.lockRules && (
                          <TableCell align="right">
                            <Typography variant="body2">
                              {player.locks || 0}
                            </Typography>
                          </TableCell>
                        )}
                        {viewMode === "year" ? (
                          <>
                            <TableCell align="right">
                              <Typography variant="body2">
                                {player.wins || 0}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">
                              <Typography variant="body2">
                                {player.losses || 0}
                              </Typography>
                            </TableCell>
                          </>
                        ) : (
                          rules?.gameOfTheWeekRules && (
                            <TableCell align="right">
                              {(() => {
                                // Find the GOTW game to check if it has finished
                                const gotwGame = selectedGames.find(
                                  (game) => game.game.id === gameOfTheWeek
                                );
                                const gotwFinished =
                                  gotwGame &&
                                  gotwGame.game.completed &&
                                  gotwGame.game.homePoints !== undefined &&
                                  gotwGame.game.awayPoints !== undefined;

                                if (player.correctGamesOfTheWeek > 0) {
                                  return (
                                    <CheckCircleIcon
                                      sx={{
                                        color: "success.main",
                                        fontSize: 20,
                                      }}
                                    />
                                  );
                                } else if (gotwFinished) {
                                  return (
                                    <CancelIcon
                                      sx={{
                                        color: "error.main",
                                        fontSize: 20,
                                      }}
                                    />
                                  );
                                } else {
                                  // GOTW game hasn't finished yet, show nothing or a neutral indicator
                                  return null;
                                }
                              })()}
                            </TableCell>
                          )
                        )}
                      </TableRow>
                    );
                  });
              })()
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
};

// Skeleton component for loading state
export const ScoreboardTableSkeleton: React.FC<{
  isMobile?: boolean;
  viewMode?: "week" | "year";
}> = ({ isMobile = false, viewMode = "week" }) => {
  const mobilePadding = isMobile ? 1.5 : 2;

  return (
    <Paper elevation={2}>
      <Box
        sx={{
          background:
            viewMode === "year"
              ? "linear-gradient(135deg, #2e7d32, #66bb6a)"
              : "linear-gradient(135deg, #ff9800, #ffb74d)",
          color: "white",
          padding: mobilePadding,
        }}
      >
        <Skeleton
          variant="text"
          width="60%"
          height={isMobile ? 20 : 24}
          sx={{ bgcolor: "rgba(255,255,255,0.3)" }}
        />
      </Box>

      {/* Winner/Loser Section Skeleton */}
      <Box sx={{ p: 3, borderBottom: "1px solid #e0e0e0" }}>
        <Skeleton
          variant="text"
          width="30%"
          height={28}
          sx={{ mb: 3, mx: "auto" }}
        />
        <Box
          sx={{
            display: "flex",
            gap: 3,
            flexDirection: { xs: "column", md: "row" },
          }}
        >
          {/* Winners skeleton */}
          <Box sx={{ flex: 1, textAlign: "center" }}>
            <Skeleton
              variant="text"
              width="50%"
              height={24}
              sx={{ mb: 2, mx: "auto" }}
            />
            <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
              <Box sx={{ display: "flex", gap: 1 }}>
                {[1, 2, 3].map((i) => (
                  <Skeleton key={i} variant="circular" width={40} height={40} />
                ))}
              </Box>
            </Box>
            <Skeleton
              variant="text"
              width="30%"
              height={20}
              sx={{ mx: "auto" }}
            />
          </Box>
          {/* Losers skeleton */}
          <Box sx={{ flex: 1, textAlign: "center" }}>
            <Skeleton
              variant="text"
              width="50%"
              height={24}
              sx={{ mb: 2, mx: "auto" }}
            />
            <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
              <Box sx={{ display: "flex", gap: 1 }}>
                {[1, 2].map((i) => (
                  <Skeleton key={i} variant="circular" width={40} height={40} />
                ))}
              </Box>
            </Box>
            <Skeleton
              variant="text"
              width="30%"
              height={20}
              sx={{ mx: "auto" }}
            />
          </Box>
        </Box>
      </Box>

      <Box sx={{ p: 2 }}>
        {/* Table header skeleton */}
        <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
          <Skeleton variant="text" width="10%" height={20} />
          <Skeleton variant="text" width="20%" height={20} />
          <Skeleton variant="text" width="15%" height={20} />
          <Skeleton variant="text" width="15%" height={20} />
          <Skeleton variant="text" width="15%" height={20} />
          <Skeleton variant="text" width="15%" height={20} />
        </Box>
        {/* Table rows skeleton */}
        {[1, 2, 3, 4, 5].map((i) => (
          <Box
            key={i}
            sx={{
              display: "flex",
              gap: 2,
              mb: 1,
              p: 1,
              bgcolor: "rgba(0,0,0,0.02)",
            }}
          >
            <Skeleton variant="text" width="10%" height={20} />
            <Skeleton variant="text" width="20%" height={20} />
            <Skeleton variant="text" width="15%" height={20} />
            <Skeleton variant="text" width="15%" height={20} />
            <Skeleton variant="text" width="15%" height={20} />
            <Skeleton variant="text" width="15%" height={20} />
          </Box>
        ))}
      </Box>
    </Paper>
  );
};

export default ScoreboardTable;
